import { useState, useCallback, useEffect } from "react";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";
import { Mail, Shield, Trash2, Eye, EyeOff, Save, Send } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";

export const EmailSection = ({ user, subscription }) => {
  const [smtpStatus, setSmtpStatus] = useState(null);
  const [appPassword, setAppPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [savingSmtp, setSavingSmtp] = useState(false);
  const [testingSmtp, setTestingSmtp] = useState(false);
  const [senderName, setSenderName] = useState("");
  const [emailSignature, setEmailSignature] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);

  const fetchSmtpStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/settings/smtp`, { withCredentials: true });
      setSmtpStatus(res.data);
      setSenderName(res.data.sender_name || "");
      setEmailSignature(res.data.email_signature || "");
    } catch {
      setSmtpStatus({ configured: false });
    }
  }, []);

  useEffect(() => { fetchSmtpStatus(); }, [fetchSmtpStatus]);

  const handleSaveSmtp = async () => {
    if (!appPassword.trim()) return;
    setSavingSmtp(true);
    try {
      await axios.put(`${API}/settings/smtp`, { gmail_app_password: appPassword }, { withCredentials: true });
      toast.success("SMTP settings saved");
      setAppPassword("");
      fetchSmtpStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save SMTP settings");
    } finally {
      setSavingSmtp(false);
    }
  };

  const handleTestSmtp = async () => {
    setTestingSmtp(true);
    try {
      const res = await axios.post(`${API}/settings/smtp/test`, { test_email: testEmail || undefined }, { withCredentials: true });
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Test email failed");
    } finally {
      setTestingSmtp(false);
    }
  };

  const handleRemoveSmtp = async () => {
    try {
      await axios.delete(`${API}/settings/smtp`, { withCredentials: true });
      toast.success("SMTP settings removed");
      fetchSmtpStatus();
    } catch {
      toast.error("Failed to remove SMTP settings");
    }
  };

  const handleSaveEmailProfile = async () => {
    setSavingProfile(true);
    try {
      await axios.put(`${API}/settings/email-profile`, { sender_name: senderName, email_signature: emailSignature }, { withCredentials: true });
      toast.success("Email profile updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save email profile");
    } finally {
      setSavingProfile(false);
    }
  };

  const isPaidPlan = subscription?.plan_id && subscription.plan_id !== "starter";

  return (
    <Card className="card-surface" data-testid="smtp-card">
      <CardHeader style={{ borderBottom: '1px solid var(--t-border)', padding: '16px 20px' }}>
        <div className="flex items-center gap-3">
          <Mail className="w-5 h-5" style={{ color: 'var(--t-text-muted)' }} />
          <div>
            <CardTitle className="text-lg font-semibold" style={{ color: 'var(--t-text)', fontFamily: 'var(--t-heading-font)' }}>
              Email Setup (Gmail SMTP)
            </CardTitle>
            <CardDescription style={{ color: 'var(--t-text-muted)' }}>
              Send emails from your own Google account using an App Password
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-5 space-y-5">
        {/* Free plan notice */}
        {!isPaidPlan && (
          <div className="p-4 rounded-xl border border-blue-200 bg-blue-50" data-testid="starter-email-notice">
            <div className="flex items-start gap-3">
              <Mail className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-semibold text-blue-900">Starter Plan — Emails via EasyOffice</p>
                <p className="text-xs text-blue-700 mt-1 leading-relaxed">
                  On the free plan, workflow emails are sent from <strong>noreply@easyoffice.systems</strong> so you can test the full email workflow.
                  Upgrade to Professional or higher to send emails from your own domain.
                </p>
                <Button
                  size="sm"
                  className="mt-3 bg-blue-600 hover:bg-blue-700 text-white text-xs h-7 px-3"
                  onClick={() => document.getElementById("subscription-section")?.scrollIntoView({ behavior: "smooth" })}
                  data-testid="upgrade-email-btn"
                >
                  Upgrade to Send From Your Domain
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* SMTP config — only for paid plans */}
        {isPaidPlan && (<>
          {/* Status */}
          <div className="flex items-center justify-between p-3 rounded-xl" style={{ background: 'var(--t-bg)', border: '1px solid var(--t-border)' }}>
            <div className="flex items-center gap-3">
              <Shield className={`w-5 h-5 ${smtpStatus?.configured ? 'text-green-600' : 'text-amber-500'}`} />
              <div>
                <p className="text-sm font-medium" style={{ color: 'var(--t-text)' }}>
                  {smtpStatus?.configured ? 'SMTP Configured' : 'SMTP Not Configured'}
                </p>
                <p className="text-xs" style={{ color: 'var(--t-text-muted)' }}>
                  {smtpStatus?.configured
                    ? `Sending as ${smtpStatus.email} (Password: ${smtpStatus.masked_password})`
                    : 'Set up your App Password to enable email sending'}
                </p>
              </div>
            </div>
            {smtpStatus?.configured && (
              <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={handleRemoveSmtp} data-testid="remove-smtp-btn">
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </div>

          {/* Instructions */}
          <div className="p-4 rounded-xl" style={{ background: 'var(--t-primary)' + '10', border: '1px solid ' + 'var(--t-primary)' + '30' }}>
            <p className="text-sm font-medium mb-2" style={{ color: 'var(--t-text)' }}>How to get a Google App Password:</p>
            <ol className="text-xs space-y-1 list-decimal list-inside" style={{ color: 'var(--t-text-muted)' }}>
              <li>Go to <a href="https://myaccount.google.com/security" target="_blank" rel="noopener noreferrer" className="underline font-medium">Google Account Security</a></li>
              <li>Enable <strong>2-Step Verification</strong> if not already enabled</li>
              <li>Go to <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noopener noreferrer" className="underline font-medium">App Passwords</a></li>
              <li>Create a new app password (name it "Transaction Manager")</li>
              <li>Copy the 16-character password and paste it below</li>
            </ol>
          </div>

          {/* App Password Input */}
          <div className="space-y-2">
            <Label htmlFor="app-password" style={{ color: 'var(--t-text)' }}>Gmail App Password</Label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Input
                  id="app-password"
                  type={showPassword ? "text" : "password"}
                  value={appPassword}
                  onChange={(e) => setAppPassword(e.target.value)}
                  placeholder="xxxx xxxx xxxx xxxx"
                  className="pr-10"
                  data-testid="input-app-password"
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--t-text-subtle)' }}>
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <Button onClick={handleSaveSmtp} disabled={!appPassword.trim() || savingSmtp} className="btn-primary" data-testid="save-smtp-btn">
                <Save className="w-4 h-4 mr-2" />
                {savingSmtp ? "Saving..." : "Save"}
              </Button>
            </div>
          </div>

          {/* Test Email */}
          {smtpStatus?.configured && (
            <div className="space-y-2">
              <Label htmlFor="test-email" style={{ color: 'var(--t-text)' }}>Send Test Email</Label>
              <div className="flex gap-2">
                <Input
                  id="test-email"
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder={smtpStatus?.email || "your@email.com"}
                  data-testid="input-test-email"
                />
                <Button variant="outline" onClick={handleTestSmtp} disabled={testingSmtp} data-testid="test-smtp-btn" style={{ borderColor: 'var(--t-border)' }}>
                  <Send className="w-4 h-4 mr-2" />
                  {testingSmtp ? "Sending..." : "Test"}
                </Button>
              </div>
              <p className="text-xs" style={{ color: 'var(--t-text-subtle)' }}>
                Leave blank to send to your own email ({smtpStatus?.email})
              </p>
            </div>
          )}

          {/* Sender Name & Signature */}
          {smtpStatus?.configured && (
            <div className="space-y-4 pt-4" style={{ borderTop: '1px solid var(--t-border)' }}>
              <div className="space-y-2">
                <Label htmlFor="sender-name" style={{ color: 'var(--t-text)' }}>Sender Name</Label>
                <Input
                  id="sender-name"
                  value={senderName}
                  onChange={(e) => setSenderName(e.target.value)}
                  placeholder={user?.name || "Your Name"}
                  data-testid="input-sender-name"
                />
                <p className="text-xs" style={{ color: 'var(--t-text-subtle)' }}>
                  Recipients will see this name in the "From" field (e.g., "Matt C Team" instead of just your email)
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="email-signature" style={{ color: 'var(--t-text)' }}>Email Signature</Label>
                <textarea
                  id="email-signature"
                  value={emailSignature}
                  onChange={(e) => setEmailSignature(e.target.value)}
                  placeholder={"Best regards,\nYour Name\nMattC Real Estate Team\n(555) 123-4567"}
                  rows={8}
                  className="flex w-full rounded-md border px-3 py-2 text-sm"
                  style={{ borderColor: 'var(--t-border)', background: 'var(--t-surface)', color: 'var(--t-text)', resize: 'vertical', minHeight: '120px' }}
                  data-testid="input-email-signature"
                />
                <p className="text-xs" style={{ color: 'var(--t-text-subtle)' }}>
                  Appended to every outbound email. Supports plain text with line breaks.
                </p>
              </div>
              <Button onClick={handleSaveEmailProfile} disabled={savingProfile} className="btn-primary" data-testid="save-email-profile-btn">
                <Save className="w-4 h-4 mr-2" />
                {savingProfile ? "Saving..." : "Save Email Profile"}
              </Button>
            </div>
          )}
        </>)}
      </CardContent>
    </Card>
  );
};
